<template>
  <section>
    <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
    <v-alert v-if="message" type="info" text aria-live="polite">{{ message }}</v-alert>
    <div v-if="!session">
      <v-btn color="primary" :disabled="!!error || startingSession" @click="startSession()">Démarrer {{ burst ? 'la rafale' : 'le scan individuel' }}</v-btn>
      <div v-for="saved in savedSessions" :key="saved.id" class="mt-3">
        Session du {{ date(saved.started_at) }} — {{ saved.scans.length }} captures
        <v-btn small :disabled="startingSession" @click="startSession(saved)">Reprendre</v-btn>
      </div>
    </div>
    <template v-else>
      <div class="mb-3">Début : {{ date(session.started_at) }} · {{ session.closed ? 'Terminée' : active ? 'En cours' : 'En pause' }}</div>
      <div class="d-flex flex-wrap mb-3" aria-live="polite">
        <v-chip class="mr-2 mb-2">Capturés : {{ session.scans.length }}</v-chip>
        <v-chip class="mr-2 mb-2" color="success" outlined>Confirmés : {{ count('confirmed') }}</v-chip>
        <v-chip class="mr-2 mb-2" outlined>En attente : {{ count('pending') + count('sending') }}</v-chip>
        <v-chip class="mr-2 mb-2" color="error" outlined>Erreurs : {{ count('error') }}</v-chip>
        <v-chip v-if="count('uncertain')" class="mr-2 mb-2" color="warning">À vérifier : {{ count('uncertain') }}</v-chip>
      </div>
      <v-btn small class="mr-2" :disabled="session.closed || !!error || locked" @click="togglePause">{{ active ? 'Pause' : 'Continuer' }}</v-btn>
      <v-btn small :disabled="session.closed" @click="finish">Terminer la capture</v-btn>
      <v-btn v-if="burst && halted" small color="warning" class="ml-2" :disabled="inFlight || !!error" @click="retry">Renvoyer les scans non confirmés</v-btn>
      <v-btn v-if="session.closed && !inFlight" small class="ml-2" @click="leaveSession">Autre session</v-btn>
      <p v-if="locked" role="status" class="mt-3">Traitement en cours… Attendez la fin du scénario avant de scanner un autre code.</p>
      <video v-if="config.camera" ref="video" class="scan-video mt-3" autoplay muted playsinline></video>
      <v-btn v-if="cameraError && active" small @click="startCamera">Réessayer la caméra</v-btn>
      <v-text-field v-model="manual" label="Scannez ou saisissez un code-barres" :disabled="!active || locked || !!error"
        :loading="locked" @keyup.enter="capture(manual, 'MANUAL')" />
      <v-btn small :disabled="!active || locked || !!error || !manual.trim()" @click="capture(manual, 'MANUAL')">Valider le code</v-btn>
      <p class="text-caption">Le signal confirme la capture locale. Seul le statut « Confirmé » indique la fin du traitement serveur.</p>
      <v-simple-table dense>
        <thead><tr><th>Heure</th><th>Code</th><th>Format</th><th>Statut</th></tr></thead>
        <tbody><tr v-for="scan in recent" :key="scan.scan_id"><td>{{ date(scan.captured_at) }}</td><td class="scan-code">{{ scan.barcode }}</td><td>{{ scan.barcode_format }}</td><td>{{ label(scan.status) }}<div>{{ scan.message }}</div></td></tr></tbody>
      </v-simple-table>
      <p v-if="session.scans.length > 20" class="text-caption">Les 20 dernières captures sont affichées ; les compteurs couvrent toute la session.</p>
    </template>
  </section>
</template>

<script>
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import api from '@/services/api';
import { FORMATS, uuid, createSession, sessionKey, saveSession, readSessions, recoverSession, applyResults, validateBatchResponse, presenceGate } from './scanSession';

export default {
  name: 'BarcodeScanner',
  props: { config: Object, idmodule: [String, Number], params: Object, querys: Object, page: [String, Number], modalKey: [String, Number], visible: { type: Boolean, default: true } },
  data: () => ({ session: null, savedSessions: [], startingSession: false, active: false, locked: false, inFlight: false, halted: false, manual: '', error: '', message: '', controls: null, cameraError: false, cameraStarting: false, cameraGeneration: 0, destroyed: false, timer: null, scenarioTimer: null, releaseLock: null, token: null, gate: presenceGate() }),
  computed: {
    burst() { return this.config.scan_mode === 'burst'; },
    recent() { return this.session ? this.session.scans.slice(-20).reverse() : []; },
  },
  watch: { visible(value) { if (value && this.active) this.startCamera(); else this.stopCamera(); } },
  mounted() {
    try { this.token = localStorage.getItem('tokenMinu'); this.loadSaved(); }
    catch (e) { this.error = 'Le stockage local est indisponible. Autorisez-le avant de démarrer une session.'; }
    window.addEventListener('storage', this.onStorage);
  },
  beforeDestroy() { this.destroyed = true; this.active = false; clearTimeout(this.timer); clearTimeout(this.scenarioTimer); this.stopCamera(); if (this.releaseLock) this.releaseLock(); this.releaseLock = null; window.removeEventListener('storage', this.onStorage); },
  methods: {
    date(value) { return new Date(value).toLocaleString(); },
    count(status) { return this.session.scans.filter(s => s.status === status).length; },
    label(status) { return { pending: 'En attente', sending: 'Traitement', confirmed: 'Confirmé', error: 'Erreur', uncertain: 'À vérifier' }[status]; },
    loadSaved() {
      try {
        if (!this.config.storage_scope) throw new Error('Contexte de scan indisponible. Rechargez la page.');
        this.savedSessions = readSessions(localStorage, this.config.storage_scope);
      } catch (e) { this.error = e.message; }
    },
    persist() {
      if (this.destroyed) return false;
      try { saveSession(localStorage, this.session); return true; }
      catch (e) { this.error = 'Stockage local indisponible ou plein. Capture arrêtée ; conservez cette page ouverte pour préserver la session.'; this.active = false; this.halted = true; this.stopCamera(); return false; }
    },
    contextValid() {
      if (this.destroyed || localStorage.getItem('tokenMinu') !== this.token) {
        this.active = false; this.halted = true; this.stopCamera();
        if (!this.destroyed) this.error = 'Connexion ou entreprise modifiée. Rechargez la page avant de reprendre.';
        return false;
      }
      return true;
    },
    onStorage(event) { if (event.key === 'tokenMinu') this.contextValid(); },
    async claim(session) {
      // Web Locks prevents two tabs resuming/writing the same durable session.
      if (!navigator.locks) {
        if (this.savedSessions.some(s => s.id === session.id)) throw new Error('La reprise sécurisée exige un navigateur compatible Web Locks (Chrome, Edge, Firefox récent).');
        return;
      }
      await new Promise((resolve, reject) => {
        navigator.locks.request(`barcode:${session.scope}:${session.id}`, { ifAvailable: true }, lock => {
          if (!lock) { reject(new Error('Cette session est déjà ouverte dans un autre onglet.')); return; }
          return new Promise(release => { this.releaseLock = release; resolve(); });
        }).catch(reject);
      });
    },
    async startSession(saved) {
      if (this.startingSession || this.session || !this.contextValid()) return;
      this.startingSession = true;
      try {
        let session = saved ? recoverSession(saved) : createSession(this.config.storage_scope, this.burst ? 'burst' : 'individual', this.params || {}, this.querys || {});
        if (session.mode !== (this.burst ? 'burst' : 'individual')) throw new Error('Cette session utilise un autre mode de scan. Rétablissez le mode du module pour la reprendre.');
        await this.claim(session);
        if (saved) session = recoverSession(JSON.parse(localStorage.getItem(sessionKey(session))));
        if (this.destroyed) { if (this.releaseLock) this.releaseLock(); return; }
        this.session = session;
        this.error = ''; this.halted = this.burst && session.scans.some(s => ['error', 'uncertain'].includes(s.status));
        if (!this.persist()) return;
        this.active = !session.closed;
        this.locked = !this.burst && session.scans.some(s => ['uncertain', 'pending'].includes(s.status));
        if (this.locked) this.message = 'Un scan individuel est sans confirmation. Vérifiez son résultat avant de commencer une nouvelle session ; il ne sera pas rejoué automatiquement.';
        await this.$nextTick(); this.startCamera(); this.schedule();
      } catch (e) { if (this.releaseLock) this.releaseLock(); this.releaseLock = null; this.message = e.message; }
      finally { this.startingSession = false; }
    },
    togglePause() { this.active = !this.active; if (this.active) this.startCamera(); else this.stopCamera(); },
    finish() { this.active = false; this.session.closed = true; this.stopCamera(); this.persist(); this.schedule(); },
    leaveSession() { clearTimeout(this.timer); this.timer = null; this.stopCamera(); this.active = false; if (this.releaseLock) this.releaseLock(); this.releaseLock = null; this.session = null; this.locked = false; this.message = ''; this.loadSaved(); },
    async startCamera() {
      if (!this.config.camera || !this.active || !this.visible || this.controls || this.cameraStarting || this.destroyed) return;
      const video = this.$refs.video;
      if (!video) return;
      const generation = ++this.cameraGeneration;
      this.cameraStarting = true; this.cameraError = false;
      try {
        const formats = (this.config.barcode_formats || ['CODE_128']).filter(f => FORMATS.includes(f));
        const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, (formats.length ? formats : ['CODE_128']).map(f => BarcodeFormat[f])]]);
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 80, delayBetweenScanSuccess: 100 });
        const controls = await reader.decodeFromConstraints({ audio: false, video: { facingMode: { ideal: 'environment' } } }, video, result => {
          if (generation !== this.cameraGeneration || !this.active || this.locked) return;
          if (!result) { this.gate.missing(); return; }
          const code = result.getText(); const format = BarcodeFormat[result.getBarcodeFormat()];
          if (this.gate.detect(code, format)) this.capture(code, format);
        });
        if (generation !== this.cameraGeneration || !this.active || this.destroyed) controls.stop(); else this.controls = controls;
      } catch (e) { this.cameraError = true; this.message = 'Caméra indisponible : vérifiez son autorisation et utilisez HTTPS, ou saisissez le code manuellement.'; }
      finally { this.cameraStarting = false; }
    },
    stopCamera() { this.cameraGeneration += 1; if (this.controls) this.controls.stop(); this.controls = null; const video = this.$refs.video; if (video && video.srcObject) { video.srcObject.getTracks().forEach(t => t.stop()); video.srcObject = null; } },
    async capture(code, format) {
      const barcode = String(code || '').trim();
      if (!barcode || !this.active || this.locked || this.error || !this.contextValid()) return;
      const scan = { scan_id: uuid(), barcode, barcode_format: format, captured_at: new Date().toISOString(), status: 'pending', message: '' };
      this.session.scans.push(scan);
      if (!this.persist()) return;
      this.manual = ''; this.message = `Capturé : ${barcode} (${format})`;
      if (navigator.vibrate) navigator.vibrate(40);
      if (this.burst) this.schedule(); else await this.sendIndividual(scan);
    },
    async request(payload) {
      if (!this.contextValid()) throw new Error('Contexte modifié.');
      const response = await api.post('displaymodule', { id: this.idmodule, params: this.session.params, querys: this.session.querys, scan_scope: this.config.storage_scope, ...payload }, { timeout: 60000 });
      return response.data;
    },
    async sendIndividual(scan) {
      this.locked = true; this.inFlight = true; scan.status = 'sending';
      if (!this.persist()) { this.inFlight = false; return; }
      this.scenarioTimer = setTimeout(() => {
        if (!this.destroyed && this.locked) this.message = 'Attente prolongée : une interaction peut être ouverte ou la connexion interrompue. Le scan reste verrouillé. Ne rescanner pas ce code sans vérifier son résultat.';
      }, 60000);
      try {
        const data = await this.request({ barcode: scan.barcode, barcode_format: scan.barcode_format });
        if (this.destroyed) return;
        if (data.ventilation) await this.checkScenario({ ventilation: [data], page: this.page, modalKey: this.modalKey, row: 0 });
        else if (!data.success) throw new Error(data.message || 'Traitement refusé.');
        if (this.destroyed || !this.contextValid()) return;
        scan.status = 'confirmed'; this.message = `Confirmé : ${scan.barcode}`; this.locked = false;
      } catch (e) {
        scan.status = 'uncertain'; scan.message = 'Résultat à vérifier. Aucun renvoi automatique.';
        this.active = false; this.stopCamera(); this.message = scan.message;
      } finally { clearTimeout(this.scenarioTimer); this.scenarioTimer = null; this.inFlight = false; if (!this.destroyed) this.persist(); }
    },
    schedule() {
      if (!this.burst || this.destroyed || this.halted || this.inFlight || !this.session) return;
      if (this.timer) return;
      this.timer = setTimeout(() => { this.timer = null; this.flush(); }, 350);
    },
    async flush() {
      if (!this.session || this.inFlight || this.halted || !this.contextValid()) return;
      const batch = this.session.scans.filter(s => s.status === 'pending').slice(0, 20);
      if (!batch.length) return;
      this.inFlight = true; batch.forEach(s => { s.status = 'sending'; });
      if (!this.persist()) { this.inFlight = false; return; }
      try {
        const data = await this.request({ scan_mode: 'burst', scan_session_id: this.session.id, scans: batch.map(s => ({ scan_id: s.scan_id, barcode: s.barcode, barcode_format: s.barcode_format, captured_at: s.captured_at })) });
        if (this.destroyed) return;
        validateBatchResponse(this.session, batch, data);
        applyResults(this.session, batch, data.results);
        if (batch.some(s => s.status !== 'confirmed')) { this.halted = true; this.message = 'Certains scans nécessitent une vérification. Les identifiants sont conservés.'; }
      } catch (e) {
        batch.forEach(s => { s.status = 'uncertain'; s.message = 'Réponse absente ; renvoi possible avec le même identifiant.'; });
        this.halted = true; this.message = e.response && e.response.data && e.response.data.message || e.message || 'Envoi interrompu.';
      } finally { this.inFlight = false; if (!this.destroyed) { this.persist(); this.schedule(); } }
    },
    retry() {
      if (!this.contextValid()) return;
      this.session.scans.forEach(s => { if (['uncertain', 'error'].includes(s.status)) s.status = 'pending'; });
      this.halted = false; if (this.persist()) this.schedule();
    },
  },
};
</script>

<style scoped>
.scan-video { display: block; width: 100%; max-height: 45vh; background: #111; border-radius: 8px; object-fit: cover; }
.scan-code { overflow-wrap: anywhere; max-width: 220px; }
</style>
